import React, { useCallback, useEffect, useState } from 'react';
import { bool, func, number, object } from 'prop-types';
import styled from 'styled-components';
import { createStructuredSelector } from 'reselect';
import { connect } from 'react-redux';

import Tooltip from 'ravenjs/lib/Tooltip';
import { get } from 'ravenjs/utils';

import { AriesIcon, StorefrontIcon } from 'components/Icons';
import { DEFAULT_COLORS } from 'constants/colors';
import { ICONS_TOOLTIP_TEXTS, LOGO_ICONS_TEXTS } from 'constants/navigation';
import { EVENTS } from 'constants/events';
import Acl from 'modules/acl';
import { APP_PERMISSIONS } from 'constants/permissions';
import { selectors as userSelectors } from 'modules/user';
import { actions as pendoActions } from 'modules/pendo';

import NotificationWidget from '../NotificationWidget/NotificationWidget';
import UserNav from '../UserNav';
import SolutionCenter from '../SolutionCenter';
import AINav from '../AINav';

const NavigationIconsContainer = styled.div`
    display: flex;
    align-items: center;
    height: 40px;
    gap: 12px;
    padding-right: ${({ showDivider }) => (showDivider ? '20px' : '5.125rem')};
    ${({ showDivider }) =>
        showDivider &&
        `
        border-right: 1px solid ${DEFAULT_COLORS.BLACK_00};
    `}
    ${({ theme }) => theme.media.down('md')`
        display: none;
    `};
`;

const NavBarRight = styled.div`
    align-items: center;
    display: flex;
    height: 100%;
`;

const SolutionShopWrapper = styled.div`
    display: flex;
    align-items: center;
    justify-content: center;
    width: 151px;
    height: 40px;
    gap: 8px;
    padding: 10px 12px;
    border-radius: 8px;
    border: 1px solid ${DEFAULT_COLORS.WHITE_3};
    cursor: pointer;
    box-sizing: border-box;

    &:hover {
        background-color: ${DEFAULT_COLORS.BLUE_7};
    }

    /* Focus styles for keyboard navigation */
    &:focus {
        outline: 2px solid ${DEFAULT_COLORS.LINK};
        outline-offset: 2px;
    }
    &:focus:not(:focus-visible) {
        outline: none;
    }
    &:focus-visible {
        outline: 2px solid ${DEFAULT_COLORS.LINK};
        outline-offset: 2px;
    }
`;

const AriesWrapper = styled.div`
    display: flex;
    align-items: center;
    justify-content: center;
    width: 94px;
    height: 40px;
    max-height: 40px;
    gap: 8px;
    padding: 12px;
    border-radius: 8px;
    border: 1px solid ${DEFAULT_COLORS.WHITE_3};
    cursor: pointer;
    background: ${DEFAULT_COLORS.WHITE};
    box-sizing: border-box;

    &:hover {
        background-color: ${DEFAULT_COLORS.BLUE_7};
    }

    /* Focus styles for keyboard navigation */
    &:focus {
        outline: 2px solid ${DEFAULT_COLORS.LINK};
        outline-offset: 2px;
    }
    &:focus:not(:focus-visible) {
        outline: none;
    }
    &:focus-visible {
        outline: 2px solid ${DEFAULT_COLORS.LINK};
        outline-offset: 2px;
    }
`;

const NotificationWrapper = styled.div`
    display: flex;
    align-items: center;
    justify-content: center;
    width: 40px;
    height: 40px;
    max-height: 40px;
    padding: 12px;
    border-radius: 8px;
    border: 1px solid ${DEFAULT_COLORS.WHITE_3};
    cursor: pointer;
    background: ${DEFAULT_COLORS.WHITE};
    box-sizing: border-box;

    &:hover {
        background-color: ${DEFAULT_COLORS.BLUE_7};
    }

    /* Focus styles for keyboard navigation - uses focus-within like AvatarWrapper */
    &:focus-within {
        outline: 2px solid ${DEFAULT_COLORS.LINK};
        outline-offset: 2px;
    }
`;

const AvatarWrapper = styled.div`
    display: flex;
    align-items: center;
    justify-content: center;
    width: 40px;
    height: 40px;
    max-height: 40px;
    gap: 8px;
    padding: 9px 12px;
    border-radius: 8px;
    border: 1px solid ${DEFAULT_COLORS.WHITE_3};
    cursor: pointer;
    background: ${DEFAULT_COLORS.WHITE};
    box-sizing: border-box;

    &:hover {
        background-color: ${DEFAULT_COLORS.BLUE_7};
    }

    &:focus-within {
        outline: 2px solid ${DEFAULT_COLORS.LINK};
        outline-offset: 2px;
    }
`;

const IconLabel = styled.span`
    font-family: 'Roboto', sans-serif;
    font-weight: 400;
    font-size: 16px;
    line-height: 100%;
    color: ${DEFAULT_COLORS.BLACK};
    white-space: nowrap;
    vertical-align: middle;
`;

const NavBarIcons = ({
    history,
    userNav,
    user,
    isSoftLogin,
    showDivider,
    sendPendoEvent,
    isNavigationV2Enabled,
    userId,
    userCompany,
}) => {
    const [isNotificationMenuOpen, setIsNotificationMenuOpen] = useState(false);
    const [isTooltipHovered, setIsTooltipHovered] = useState(false);
    const [tooltipAllowed, setTooltipAllowed] = useState(true);

    // When cursor leaves icon area while dropdown is open, disable tooltip
    const handleNotificationMouseLeave = useCallback(() => {
        setIsTooltipHovered(false);
        if (isNotificationMenuOpen) {
            setTooltipAllowed(false);
        }
    }, [isNotificationMenuOpen]);

    // When the dropdown closes, re-enable tooltip for next hover
    useEffect(() => {
        if (!isNotificationMenuOpen) {
            setTooltipAllowed(true);
        }
    }, [isNotificationMenuOpen]);

    // Tooltip shows when cursor is over icon AND tooltip is allowed
    const showNotificationTooltip = isTooltipHovered && tooltipAllowed;

    const onUserNavClick = navLink => {
        const pendoEvent = EVENTS.NAVIGATION.SELECT;

        pendoEvent.eventProperties.selection = `usernav:${navLink.state}`;
        sendPendoEvent(pendoEvent);
    };

    const showContentAi = Acl.check(APP_PERMISSIONS.aiDigitalAssistantView);
    const skus = get(userCompany, 'skus', []);

    const companyHasPremiumSKU = skus => {
        return Boolean(skus.map(({ key }) => key).includes('premium'));
    };

    const isMICoreCompany = skus => {
        return ['mnlintelligence', 'compliance', 'platformlite'].every(element =>
            skus.map(({ key }) => key).includes(element)
        );
    };

    const isClientAdmin = Acl.check(APP_PERMISSIONS.systemCompanyAdmin);
    const hasPremiumSKU = companyHasPremiumSKU(skus);
    const hasMICoreSKU = isMICoreCompany(skus);
    const showSolutionsCenterIcon = isClientAdmin && hasPremiumSKU && !hasMICoreSKU;

    const handleSolutionShopClick = () => {
        sendPendoEvent(EVENTS.SOLUTION_SHOP.SOLUTION_SHOP_ICON);
        setTimeout(() => {
            window.location.href = '/solution-shop';
        }, 1000);
    };

    const handleAriesClick = () => {
        window.location.href = '/aries';
    };

    if (!isNavigationV2Enabled) {
        return (
            <NavBarRight>
                {showContentAi && <AINav />}
                {showSolutionsCenterIcon && <SolutionCenter sendPendoEvent={sendPendoEvent} />}
                <NotificationWidget history={history} key={userId} />
                {userNav.show && (
                    <UserNav
                        history={history}
                        items={isSoftLogin ? {} : userNav.items}
                        onNavClick={navLink => {
                            onUserNavClick(navLink);
                        }}
                        user={user}
                    />
                )}
            </NavBarRight>
        );
    }

    return (
        <NavigationIconsContainer showDivider={showDivider}>
            {showSolutionsCenterIcon && (
                <Tooltip
                    id="tooltip-solution-shop"
                    content={ICONS_TOOLTIP_TEXTS.SOLUTION_SHOP}
                    padding="4px 16px"
                    margin={0}
                    arrowSize={5}
                    placement="bottom"
                    borderRadius="2px"
                >
                    <SolutionShopWrapper
                        onClick={handleSolutionShopClick}
                        tabIndex={0}
                        // Added a keyboard handler for Enter/Space
                        onKeyDown={e => {
                            if (e.key === 'Enter' || e.key === ' ') {
                                e.preventDefault();
                                handleSolutionShopClick();
                            }
                        }}
                        role="button"
                        aria-label="Solution Shop"
                    >
                        <StorefrontIcon
                            fill={DEFAULT_COLORS.BLACK}
                            width="20px"
                            height="20px"
                            cursor="pointer"
                        />
                        <IconLabel>{LOGO_ICONS_TEXTS.SOLUTION_SHOP}</IconLabel>
                    </SolutionShopWrapper>
                </Tooltip>
            )}

            {showContentAi && (
                <Tooltip
                    id="tooltip-aries"
                    content={ICONS_TOOLTIP_TEXTS.ARIES}
                    padding="4px 16px"
                    margin={0}
                    arrowSize={5}
                    placement="bottom"
                    borderRadius="2px"
                >
                    <AriesWrapper
                        // Added tabIndex for keyboard navigation
                        onClick={handleAriesClick}
                        tabIndex={0}
                        // Added a keyboard handler for Enter/Space
                        onKeyDown={e => {
                            if (e.key === 'Enter' || e.key === ' ') {
                                e.preventDefault();
                                handleAriesClick();
                            }
                        }}
                        role="button"
                        aria-label="Aries AI Assistant"
                    >
                        <AriesIcon
                            fill={DEFAULT_COLORS.CHAT_AVATAR_BASE}
                            width="20px"
                            height="20px"
                            cursor="pointer"
                        />
                        <IconLabel>{LOGO_ICONS_TEXTS.ARIES}</IconLabel>
                    </AriesWrapper>
                </Tooltip>
            )}

            <NotificationWrapper>
                <NotificationWidget
                    history={history}
                    key={userId}
                    onMenuOpenChange={setIsNotificationMenuOpen}
                    tooltipText={ICONS_TOOLTIP_TEXTS.BELL}
                    showTooltip={showNotificationTooltip}
                    onTooltipHover={() => setIsTooltipHovered(true)}
                    onTooltipLeave={handleNotificationMouseLeave}
                />
            </NotificationWrapper>

            {userNav.show && (
                <AvatarWrapper>
                    <UserNav
                        history={history}
                        items={isSoftLogin ? {} : userNav.items}
                        onNavClick={navLink => {
                            onUserNavClick(navLink);
                        }}
                        user={user}
                    />
                </AvatarWrapper>
            )}
        </NavigationIconsContainer>
    );
};

NavBarIcons.propTypes = {
    history: object,
    userNav: object,
    user: object,
    isSoftLogin: bool,
    showDivider: bool,
    isNavigationV2Enabled: bool,
    userId: number.isRequired,
    sendPendoEvent: func.isRequired,
    userCompany: object.isRequired,
};

NavBarIcons.defaultProps = {
    history: {},
    userNav: {},
    user: {},
    isSoftLogin: false,
    showDivider: false,
    isNavigationV2Enabled: false,
};

const mapStateToProps = createStructuredSelector({
    userCompany: userSelectors.getUserCompany,
    userPermissions: userSelectors.getUserPermissionsActual,
});

const mapDispatchToProps = {
    sendPendoEvent: pendoActions.sendPendoEvent,
};

export { NavBarIcons as NavBarIconsUnwrapped };
export default connect(mapStateToProps, mapDispatchToProps)(NavBarIcons);
